import { ContainerColumnRight } from '@occc-app/design-system';
import { Typography, Card, CardContent, CardHeader, InfoBox } from '@occc/ui-design-system';
import { Audience_Status } from '@occc/graphql';

import { useAudienceSizeCalculation } from './useAudienceSizeCalculation';
import { AudienceSizeSubheader } from './AudienceSizeSubheader';
import { AudienceDraftSizeContent } from './AudienceDraftSizeContent';
import { separateEveryThousandByComma } from '../../../lib/numbers';

const DAY_MS = 1000 * 60 * 60 * 24;

type AudienceTotalSizeProps = {
  audienceMembershipCount: number;
  audienceUuid: string;
  status: Audience_Status;
  updatedAt?: string | null;
  draftCount?: number | null;
  draftCountCalculationRequestedAt?: string | null;
  lastDraftCountCalculationAt?: string | null;
};

export const AudienceTotalSize = ({
  audienceMembershipCount,
  audienceUuid,
  status,
  updatedAt,
  draftCount,
  draftCountCalculationRequestedAt,
  lastDraftCountCalculationAt,
}: AudienceTotalSizeProps) => {
  const now = new Date();
  const isDraft = status === Audience_Status.Draft;
  const isActive = status === Audience_Status.Active;

  let isWaitingForActualCalculation = false;
  let actualAsOf: Date | undefined;

  if (updatedAt) {
    // The audience.updatedAt is not updated when the membershipCount value changes
    // because the membershipCount comes from another table via a join.
    const updated = new Date(updatedAt);
    isWaitingForActualCalculation = isActive
      ? !audienceMembershipCount && now.getTime() - updated.getTime() < DAY_MS
      : false;
    actualAsOf = isActive ? now : updated;
  }

  const {
    displayCount,
    isLoading,
    isCalculating,
    showInfoMessage,
    handleCalculateOrRefresh,
    hasCalculatedCount,
    neverCalculated,
  } = useAudienceSizeCalculation({
    audienceMembershipCount,
    audienceUuid,
    status,
    draftCount,
    draftCountCalculationRequestedAt,
    lastDraftCountCalculationAt,
  });

  const title = isDraft ? 'Projected Audience Size' : 'Current Audience Size';

  return (
    <ContainerColumnRight>
      <Card>
        <CardHeader
          title={
            <Typography variant='h6' component='h4' display='flex' alignItems='center'>
              {title}
            </Typography>
          }
          subheader={
            <AudienceSizeSubheader
              isDraft={isDraft}
              isCalculating={isCalculating}
              lastDraftCountCalculationAt={lastDraftCountCalculationAt}
              actualAsOf={actualAsOf}
            />
          }
          sx={{ pb: 1 }}
        />

        <CardContent>
          {isDraft ? (
            <AudienceDraftSizeContent
              displayCount={displayCount}
              isLoading={isLoading}
              isCalculating={isCalculating}
              showInfoMessage={showInfoMessage}
              neverCalculated={neverCalculated}
              hasCalculatedCount={hasCalculatedCount}
              onCalculateOrRefresh={handleCalculateOrRefresh}
            />
          ) : (
            <>
              {!isWaitingForActualCalculation && Number.isInteger(audienceMembershipCount) && (
                <Typography component='span' variant='h4'>
                  {separateEveryThousandByComma(audienceMembershipCount)}
                </Typography>
              )}
              {isWaitingForActualCalculation && (
                <InfoBox type='info' sx={{ mt: 1 }}>
                  <Typography variant='body2'>
                    Members may appear today; full audience processes overnight.
                  </Typography>
                </InfoBox>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </ContainerColumnRight>
  );
};
